import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axiosInstance from "../../../interceptors";

// Stable color palette assigned to rounds by their position so the bar +
// chips stay coherent across renders. Reused from RoundBuilder vocabulary.
const ROUND_COLORS = ["#7B5AC4", "#8B5CF6", "#A78BFA", "#EC4899", "#F59E0B", "#10B981"];

const Screening = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const jobId = params.get("jobId") ? Number(params.get("jobId")) : null;

  const [rounds, setRounds] = useState([]);
  const [weightsSum, setWeightsSum] = useState(0);
  const [weightsValid, setWeightsValid] = useState(false);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsError, setRoundsError] = useState(null);

  const [toggles, setToggles] = useState({
    tabSwitch: true,
    fullscreen: true,
    facePresence: true,
    copyPaste: true,
  });
  const [settings, setSettings] = useState({
    language: "English",
    camera: "Required",
    aiFollowups: "Aggressive",
    randomize: "Per candidate",
  });

  useEffect(() => { document.title = "Screening - Ample Hire"; }, []);

  useEffect(() => {
    if (!jobId) return;
    setRoundsLoading(true);
    setRoundsError(null);
    axiosInstance
      .post("/job-posting/define-interviews/get-all", { jobId: String(jobId) })
      .then((r) => {
        setRounds(Array.isArray(r.data?.interviewRounds) ? r.data.interviewRounds : []);
        setWeightsSum(Number(r.data?.weightsSum) || 0);
        setWeightsValid(!!r.data?.weightsSumValid);
      })
      .catch((e) => setRoundsError(e?.message || "Failed to load rounds"))
      .finally(() => setRoundsLoading(false));
  }, [jobId]);

  const jobTitle = jobId ? `Job #${jobId}` : "No job selected";

  const t = (k) => setToggles((s) => ({ ...s, [k]: !s[k] }));
  const s = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  return (
    <div className="app-content page-enter" style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 24px 32px" }}>
      <div style={hdrRow}>
        <div>
          <h1 style={hdrTitle}>Screening and proctoring</h1>
          <p style={hdrSub}>{jobTitle} · configure AI monitoring and evaluation</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btnGhost}>Preview candidate view</button>
          <button style={btnPrimary}>Save config</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} style={card}>
            <div style={cardLabel}>Integrity monitoring</div>
            {[
              { k: "tabSwitch", title: "Tab switch detection", sub: "Flag candidates who navigate away from the interview tab." },
              { k: "fullscreen", title: "Fullscreen enforcement", sub: "Require fullscreen for the duration of the session." },
              { k: "facePresence", title: "Face presence check", sub: "Detect missing face or multiple faces during recording." },
              { k: "copyPaste", title: "Copy-paste detection", sub: "Capture clipboard events on coding-style answers." },
            ].map((row) => (
              <div key={row.k} style={toggleRow}>
                <div>
                  <div style={{ fontSize: 13, color: "#0F172A", fontWeight: 600 }}>{row.title}</div>
                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>{row.sub}</div>
                </div>
                <Toggle on={toggles[row.k]} onClick={() => t(row.k)} />
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.05 }} style={card}>
            <div style={cardLabel}>Settings</div>
            <Row>
              <Field label="Language"><Select v={settings.language} onChange={(v) => s("language", v)} options={["English", "Hindi", "Spanish", "French"]} /></Field>
              <Field label="Camera"><Select v={settings.camera} onChange={(v) => s("camera", v)} options={["Required", "Optional", "Off"]} /></Field>
            </Row>
            <Row>
              <Field label="AI follow-ups"><Select v={settings.aiFollowups} onChange={(v) => s("aiFollowups", v)} options={["Off", "Light", "Aggressive"]} /></Field>
              <Field label="Randomize"><Select v={settings.randomize} onChange={(v) => s("randomize", v)} options={["No", "Per candidate", "Per question set"]} /></Field>
            </Row>
          </motion.div>
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 }} style={card}>
            <div style={cardLabel}>AI evaluation layers</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { icon: "🎯", title: "Competency radar", sub: "6-dimension scoring" },
                { icon: "🧠", title: "Personality fit", sub: "Big Five + culture" },
                { icon: "🔮", title: "Predictive analytics", sub: "Success probability" },
                { icon: "🗣️", title: "Voice analysis", sub: "Pitch, fluency, speed" },
              ].map((c, i) => (
                <div key={i} style={featureCard}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>{c.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>{c.title}</div>
                  <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>{c.sub}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.15 }} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={cardLabel}>Scoring weights</div>
              {jobId && (
                <button
                  onClick={() => nav(`/admin/jobs/${jobId}/rounds`)}
                  style={{ background: "none", border: "none", color: "#7B5AC4", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0 }}
                >
                  Configure rounds →
                </button>
              )}
            </div>

            {!jobId ? (
              <div style={{ fontSize: 12, color: "#94A3B8", padding: "12px 0" }}>
                Open this page with <code style={{ background: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>?jobId=&lt;id&gt;</code> to see real per-round weights.
              </div>
            ) : roundsLoading ? (
              <div style={{ fontSize: 12, color: "#94A3B8", padding: "12px 0" }}>Loading rounds…</div>
            ) : roundsError ? (
              <div style={{ fontSize: 12, color: "#B91C1C", padding: "12px 0" }}>{roundsError}</div>
            ) : rounds.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94A3B8", padding: "12px 0" }}>
                No rounds defined for this job yet. <button
                  onClick={() => nav(`/admin/jobs/${jobId}/rounds`)}
                  style={{ background: "none", border: "none", color: "#7B5AC4", fontWeight: 600, cursor: "pointer", padding: 0 }}
                >Add some</button>.
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 13, color: "#0F172A", flexWrap: "wrap" }}>
                  {rounds.map((r, i) => (
                    <WeightChip
                      key={`${r.jobId}-${r.interviewRound}`}
                      pct={r.weightPct != null ? `${Number(r.weightPct).toFixed(0)}%` : "—"}
                      label={r.interviewRound}
                      color={ROUND_COLORS[i % ROUND_COLORS.length]}
                    />
                  ))}
                </div>
                <div style={{ marginTop: 12, height: 8, borderRadius: 4, overflow: "hidden", display: "flex", background: "#F1F5F9" }}>
                  {rounds.map((r, i) => {
                    const w = Number(r.weightPct) || 0;
                    if (w <= 0) return null;
                    return (
                      <div
                        key={`bar-${r.jobId}-${r.interviewRound}`}
                        style={{ width: `${w}%`, background: ROUND_COLORS[i % ROUND_COLORS.length] }}
                      />
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: weightsValid ? "#065F46" : "#92400E" }}>
                  Sum: {weightsSum.toFixed(0)} / 100{weightsValid ? " ✓" : " — needs to equal 100"}
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ on, onClick }) => (
  <button onClick={onClick} aria-pressed={on} style={{
    width: 32, height: 18, borderRadius: 9, border: "none", padding: 0, position: "relative",
    background: on ? "#10B981" : "#CBD5E1", cursor: "pointer", transition: "background 0.18s",
  }}>
    <span style={{
      position: "absolute", top: 2, left: on ? 16 : 2, width: 14, height: 14, borderRadius: 7,
      background: "white", boxShadow: "0 1px 2px rgba(0,0,0,0.18)", transition: "left 0.18s",
    }} />
  </button>
);

const Select = ({ v, onChange, options }) => (
  <select value={v} onChange={(e) => onChange(e.target.value)} style={input}>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const WeightChip = ({ pct, label, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", lineHeight: 1 }}>{pct}</div>
      <div style={{ fontSize: 10, color: "#64748B" }}>{label}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 10, color: "#64748B", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    {children}
  </div>
);
const Row = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 4 }}>{children}</div>;
const card = { background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16 };
const cardLabel = { fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 };
const input = { width: "100%", padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 13, color: "#0F172A", outline: "none", background: "white", boxSizing: "border-box" };
const hdrRow = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 };
const hdrTitle = { fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 };
const hdrSub = { fontSize: 13, color: "#64748B", margin: "4px 0 0" };
const btnPrimary = { padding: "9px 18px", borderRadius: 6, background: "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer" };
const btnGhost = { padding: "8px 14px", borderRadius: 6, background: "white", color: "#475569", border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 12, cursor: "pointer" };
const toggleRow = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F1F5F9" };
const featureCard = { background: "#F8FAFC", borderRadius: 10, padding: "14px 12px", textAlign: "center", border: "1px solid #E2E8F0" };

export default Screening;
